import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import API from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('finka_token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await API.get('/auth/me');
      setUser(res.data);
    } catch {
      localStorage.removeItem('finka_token');
      localStorage.removeItem('finka_user');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  const login = async (email, password) => {
    const res = await API.post('/auth/login', { email, password });
    localStorage.setItem('finka_token', res.data.token);
    localStorage.setItem('finka_user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = () => {
    localStorage.removeItem('finka_token');
    localStorage.removeItem('finka_user');
    setUser(null);
  };

  const hasPermission = (perm) => {
    if (!user) return false;
    return user.permissions?.includes(perm);
  };

  const hasAnyPermission = (perms) => {
    if (!user) return false;
    return perms.some(p => user.permissions?.includes(p));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, hasPermission, hasAnyPermission, loadUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};
